import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, Building2, User, Phone, Mail, FileText, Download, Printer, Filter } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Table from '../../components/ui/Table';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import Combobox from '../../components/ui/Combobox';
import AddressBookModal from '../../components/address-book/AddressBookModal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { exportAddressBookBookletPDF, exportToExcel } from '../../utils/exportUtils';
import { useAuth } from '../../context/AuthContext';
import { useUIStore } from '../../store/useUIStore';

const AddressBook = () => {
  const { user } = useAuth();
  const { tableDensity, setTableDensity } = useUIStore();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [prefillContact, setPrefillContact] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  const [deleteId, setDeleteId] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [linkTarget, setLinkTarget] = useState(null);
  const [linkUserId, setLinkUserId] = useState('');
  const [linkSaving, setLinkSaving] = useState(false);

  const fetchContacts = async (searchTerm = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      params.set('includeUsers', '1');
      const query = `?${params.toString()}`;
      const response = await fetch(`/api/address-book${query}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setContacts(data.data);
      } else {
        toast.error(data.message || 'Failed to fetch contacts');
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchContacts(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleCreate = () => {
    setEditingContact(null);
    setPrefillContact(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEdit = (contact) => {
    if (contact?.sourceType === 'user') {
      setEditingContact(null);
      setPrefillContact({
        category: 'Internal Organization',
        organizationName: contact.organizationName || contact.displayName || contact.contactPerson || '',
        contactPerson: contact.contactPerson || contact.displayName || contact.organizationName || '',
        designation: contact.designation || '',
        address: contact.address || '',
        email: contact.email || '',
        phoneNumber: contact.phoneNumber || '',
        linkedUserId: String(contact.linkedUserId || contact.sourceId || contact.id || '')
      });
      setModalMode('clone');
      setIsModalOpen(true);
      return;
    }
    setPrefillContact(null);
    setModalMode('edit');
    setEditingContact(contact);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
  };

  const handleOpenLinkModal = (contact) => {
    setLinkTarget(contact);
    setLinkUserId(contact?.linkedUserId ? String(contact.linkedUserId) : '');
  };

  const handleSaveLink = async () => {
    if (!linkTarget?.id) return;
    setLinkSaving(true);
    try {
      const response = await fetch(`/api/address-book/${linkTarget.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ linkedUserId: linkUserId || null })
      });
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || 'Failed to update link');
      }
      toast.success(linkUserId ? 'Contact linked to user' : 'User link removed');
      setLinkTarget(null);
      setLinkUserId('');
      fetchContacts(search);
    } catch (error) {
      console.error('Error updating contact link:', error);
      toast.error(error?.message || 'Failed to update link');
    } finally {
      setLinkSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await fetch(`/api/address-book/${deleteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Contact deleted successfully');
        fetchContacts(search);
      } else {
        toast.error(data.message || 'Failed to delete contact');
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Error connecting to server');
    } finally {
      setDeleteId(null);
    }
  };

  const handleSuccess = () => {
    fetchContacts(search);
    setIsModalOpen(false);
    setEditingContact(null);
    setPrefillContact(null);
    setModalMode('create');
    toast.success(modalMode === 'edit' ? 'Contact updated' : 'Contact created');
  };

  const filteredContacts = (Array.isArray(contacts) ? contacts : []).filter((c) => {
    if (!categoryFilter) return true;
    return String(c?.category || '').toLowerCase() === String(categoryFilter).toLowerCase();
  });

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    ...Array.from(new Set((Array.isArray(contacts) ? contacts : []).map(c => String(c?.category || '').trim()).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({ value: v, label: v }))
  ];

  const linkableUsers = (Array.isArray(contacts) ? contacts : [])
    .filter((c) => c?.sourceType === 'user')
    .map((u) => ({
      value: String(u.sourceId || u.id),
      label: `${u.displayName || u.contactPerson || u.organizationName || 'User'}${u.designation ? ` (${u.designation})` : ''}`
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleExportExcel = () => {
    const rows = filteredContacts.map((c) => ({
      Category: c.category || '',
      Organization: c.organizationName || '',
      'Contact Person': c.contactPerson || '',
      Designation: c.designation || '',
      Email: c.email || '',
      Phone: c.phoneNumber || '',
      Address: c.address || ''
    }));
    exportToExcel(rows, 'Address_Book');
    toast.success('Excel exported successfully');
  };

  const handleExportPDF = async (output = 'download') => {
    try {
      const result = await exportAddressBookBookletPDF(filteredContacts, {
        officeName: user?.officeName || '',
        title: 'Address Book',
        output
      });
      if (output === 'download') {
        toast.success('PDF exported successfully');
      } else if (result?.mode === 'print-fallback-download') {
        toast.success('Popup blocked. PDF downloaded instead.');
      } else {
        toast.success('Print preview opened');
      }
    } catch (e) {
      void e;
      toast.error(output === 'print' ? 'Failed to print' : 'Failed to export PDF');
    }
  };

  const columns = [
    {
      header: 'Organization / Contact',
      accessorKey: 'organizationName',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900 flex items-center gap-2">
            {row.category === 'Government' ? <Building2 className="w-4 h-4 text-blue-500" /> : <User className="w-4 h-4 text-green-500" />}
            {row.displayName || row.organizationName || row.contactPerson}
            {row.sourceType === 'user' && (
              <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">User</span>
            )}
            {row.sourceType === 'address_book' && row.isLinkedUser && (
              <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Linked</span>
            )}
          </span>
          {row.organizationName && row.contactPerson && !/^[\d\s()[\]{}.,/\\_-]+$/u.test(String(row.contactPerson).trim()) && row.contactPerson !== row.displayName && (
            <span className="text-xs text-gray-500 ml-6">Attn: {row.contactPerson}</span>
          )}
          {row.designation && <span className="text-xs text-gray-400 ml-6">{row.designation}</span>}
        </div>
      )
    },
    {
      header: 'Category',
      accessorKey: 'category',
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          row.category === 'Government' ? 'bg-blue-100 text-blue-800' :
          row.category === 'Vendor' ? 'bg-purple-100 text-purple-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {row.category}
        </span>
      )
    },
    {
      header: 'Contact Info',
      accessorKey: 'email',
      render: (row) => (
        <div className="flex flex-col gap-1 text-sm text-gray-600">
          {row.email && (
            <div className="flex items-center gap-2">
              <Mail className="w-3 h-3" />
              {row.email}
            </div>
          )}
          {row.phoneNumber && (
            <div className="flex items-center gap-2">
              <Phone className="w-3 h-3" />
              {row.phoneNumber}
            </div>
          )}
        </div>
      )
    },
    {
      header: 'Address',
      accessorKey: 'address',
      className: 'max-w-xs',
      render: (row) => <span title={row.address} className="block max-w-xs text-sm text-gray-600 break-words [overflow-wrap:anywhere]">{row.address || '-'}</span>
    },
    {
      header: 'Actions',
      render: (row) => (
        row.sourceType === 'user' ? (
          <span className="text-xs text-gray-400">System</span>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleOpenLinkModal(row);
              }}
              className={`px-2 py-1 text-xs rounded-md transition-colors ${row.isLinkedUser ? 'text-indigo-700 bg-indigo-50 hover:bg-indigo-100' : 'text-slate-700 bg-slate-100 hover:bg-slate-200'}`}
              title={row.isLinkedUser ? 'Change linked user' : 'Link to user'}
            >
              {row.isLinkedUser ? 'Linked' : 'Link'}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row);
              }}
              className="p-1 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
              title={row.sourceType === 'user' ? 'Copy to Address Book' : 'Edit'}
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(row.id);
              }}
              className="p-1 text-red-600 hover:bg-red-50 rounded-full transition-colors"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )
      )
    }
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Address Book</h1>
          <p className="text-sm text-gray-500 mt-1">Manage external contacts for Dispatch and Diary</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
            <Button
              size="sm"
              variant={tableDensity === 'comfortable' ? 'primary' : 'ghost'}
              onClick={() => setTableDensity('comfortable')}
            >
              Comfortable
            </Button>
            <Button
              size="sm"
              variant={tableDensity === 'compact' ? 'primary' : 'ghost'}
              onClick={() => setTableDensity('compact')}
            >
              Compact
            </Button>
          </div>
          <Button
            variant="secondary"
            onClick={() => handleExportPDF('download')}
          >
            <FileText className="w-4 h-4 mr-2 text-red-500" />
            PDF
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleExportPDF('print')}
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          <Button
            variant="secondary"
            onClick={handleExportExcel}
          >
            <Download className="w-4 h-4 mr-2 text-green-600" />
            Excel
          </Button>
          <Button onClick={handleCreate} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            New Contact
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-12 mb-4">
          <div className="md:col-span-7">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search contacts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
              />
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="flex items-center gap-2">
              <div className="shrink-0 text-gray-400">
                <Filter className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full h-10 px-3 py-2 text-sm border rounded-lg border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {categoryOptions.map(opt => (
                    <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <User className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p>No contacts found.</p>
            {(search || categoryFilter) && <p className="text-xs mt-1">Try adjusting your search or filter.</p>}
          </div>
        ) : (
          <Table
            columns={columns}
            data={filteredContacts}
            onRowClick={(row) => row.sourceType === 'address_book' ? handleEdit(row) : undefined}
            className="border-0 shadow-none"
            density={tableDensity}
          />
        )}
      </div>

      <AddressBookModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleSuccess}
        contact={editingContact}
        prefillContact={prefillContact}
      />

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Contact"
        message="Are you sure you want to delete this contact? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <Modal
        isOpen={!!linkTarget}
        onClose={() => {
          if (linkSaving) return;
          setLinkTarget(null);
          setLinkUserId('');
        }}
        title={linkTarget?.isLinkedUser ? 'Update User Link' : 'Link Contact To User'}
        size="md"
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            <div className="font-medium text-gray-800">{linkTarget?.displayName || linkTarget?.organizationName || linkTarget?.contactPerson || 'Contact'}</div>
            <div className="text-xs mt-1">Choose a system user to enable user-ID based forwarding.</div>
          </div>

          <Combobox
            label="Linked User"
            options={[{ value: '', label: 'No linked user' }, ...linkableUsers]}
            value={linkUserId}
            onChange={setLinkUserId}
            placeholder="Select user"
          />

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setLinkTarget(null);
                setLinkUserId('');
              }}
              disabled={linkSaving}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSaveLink} disabled={linkSaving}>
              {linkSaving ? 'Saving...' : 'Save Link'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AddressBook;
